import { useDisclosure } from '@mantine/hooks';
import { modals } from '@mantine/modals';
import classNames from 'classnames/bind';
import { useCompanyConfiguration } from 'init/use-company-configuration';
import { useProfile } from 'init/use-enforce-profile';
import { i18nKeys, useTranslation } from 'locales';
import { type ReactNode, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { isEmpty } from 'remeda';
import { Icon, IconName } from 'shared/components/Icon';
import { useUpdateViews } from 'shared/hooks/use-update-views';
import { useIsMobile } from 'shared/hooks/utils/use-is-mobile';
import { Button, ButtonColor, Input } from 'shared/io';
import { useGetConstants, useGetViewsByResource } from 'shared/utils/selectors';
import type { Filters } from 'types/genericType';
import type { ResourceType } from 'types/resource-types';
import type { Pagination } from 'types/storeTypes';
import ChooseColumnModal from '../ChooseColumnModal';
import AddNewView from './AddNewView';
import type { TableRow } from './CustomTableGrid/types';
import CustomTableTitle from './CustomTableTitle';
import styleIdentifiers from './customTable.module.scss';
import { FilterItem } from './FilterItem';
import { FiltersDrawer } from './FiltersDrawer';
import { buildVisibleFilters } from './filters/filterChips';
import { buildFilterFieldAttributes, countActiveFilters } from './filters/filterParams';
import { useDocumentTitle } from './hooks/useDocumentTitle';
import { useRowSelection } from './hooks/useRowSelection';
import { useTableFilters } from './hooks/useTableFilters';
import { useTableViews } from './hooks/useTableViews';
import { useViewFilters } from './hooks/useViewFilters';
import { TableFilters } from './TableFilters/TableFilters';
import { ViewsSkeleton } from './ViewsSkeleton';
import { ViewTableGrid } from './ViewTableGrid/ViewTableGrid';

const styles = classNames.bind(styleIdentifiers);

type Props = {
  actions?: ReactNode;
  actionsCol?: Function;
  callbackAction: Function;
  className?: string;
  currentPage?: number;
  handleFilters?: (newFilters: Filters<any>, currentFilters: Filters<any>) => false | Filters<any>;
  isLoading?: boolean;
  items: any[];
  noResultMessage?: string;
  onChangeSelectionCallback?: (selectedItems: Array<unknown>) => void;
  onChangeSelectItemsAcrossAllPagesCallback?: (value: boolean) => void;
  onClickRow: (item: any, e: React.MouseEvent<HTMLTableRowElement>) => void;
  pagination?: Pagination;
  tableName: ResourceType;
  title: string;
};

export default function CustomTableViews({
  actions,
  actionsCol,
  callbackAction,
  className,
  currentPage,
  handleFilters,
  isLoading,
  items,
  noResultMessage,
  onChangeSelectionCallback,
  onChangeSelectItemsAcrossAllPagesCallback,
  onClickRow,
  pagination,
  tableName,
  title,
}: Props) {
  const { t, currentLang } = useTranslation();
  const company = useCompanyConfiguration();

  const location = useLocation();
  const profile = useProfile();

  const companyName = company.name;
  const constants = useGetConstants();
  const viewsByResource = useGetViewsByResource();
  const isMobile = useIsMobile();

  const {
    filters,
    seedFilters,
    availableFilters,
    setAvailableFilters,
    filtersName,
    replaceFiltersName,
    mergeFilterName,
    filterObject,
    applyFilters,
    setPage,
    setPageLimit,
    submit,
    setOrder,
    removeFilter,
    resetFilters,
  } = useTableFilters({ handleFilters });

  const {
    selectedIds,
    areAllItemsOnThisPageSelected,
    areAllItemsAcrossPagesSelected,
    toggleAcrossPages,
    toggleRow,
    toggleSelectAllOnPage,
  } = useRowSelection({
    items,
    onChangeSelection: onChangeSelectionCallback,
    onChangeAcrossPages: onChangeSelectItemsAcrossAllPagesCallback,
  });

  const currentView = useMemo(
    () => viewsByResource[tableName]?.find(view => view.id === (location.hash.slice(1) as any)),
    [viewsByResource, location, tableName],
  );

  const isFetching = Boolean(isLoading) && !isEmpty(items);

  useDocumentTitle(title, companyName);

  const { refreshViews } = useTableViews({ tableName, setAvailableFilters });
  const { updateViews } = useUpdateViews();

  useViewFilters({ currentView, seedFilters, replaceFiltersName });

  const filtersToShow = useMemo(
    () => buildVisibleFilters({ filters, availableFilters, constants, t }),
    [filters, availableFilters, constants, currentLang, t],
  );

  const [filtersOpened, { open: openFilters, close: closeFilters }] = useDisclosure(false);

  if (currentView == null)
    return <ViewsSkeleton title={title} actions={actions} className={className} />;

  // ================= HANDLERS ====================

  const setColumn = () => {
    modals.open({
      size: '900px',
      title: t(i18nKeys.CHOOSE_COLUMNS),
      children: (
        <ChooseColumnModal
          currentView={currentView!}
          tableName={tableName}
          callbackAction={callbackAction}
          onSaved={() => {
            modals.closeAll();
            refreshViews();
          }}
        />
      ),
    });
  };

  const filtersNumber = countActiveFilters(filters);

  const saveFilters = () => {
    if (viewsByResource[tableName][0].id === currentView.id) {
      modals.open({
        size: 'sm',
        title: t(i18nKeys.ADD_VIEW),
        children: (
          <AddNewView
            onSubmit={({ name }: any) => {
              updateViews(
                {
                  views_attributes: [
                    {
                      name,
                      view_type: tableName,
                      filter_fields_attributes: buildFilterFieldAttributes(
                        filters,
                        currentView.filter_fields,
                        availableFilters,
                      ),
                    },
                  ],
                  resourceType: tableName,
                },
                {
                  onSuccess: () => {
                    modals.closeAll();
                    refreshViews();
                  },
                },
              );
            }}
          />
        ),
      });
    } else {
      updateViews(
        {
          views_attributes: [
            {
              id: currentView.id,
              filter_fields_attributes: buildFilterFieldAttributes(
                filters,
                currentView.filter_fields,
                availableFilters,
              ),
            },
          ],
          resourceType: tableName,
        },
        { onSuccess: refreshViews },
      );
    }
  };

  // MARK: RETURN
  return (
    <div className={styles('CustomTable', 'views', className)}>
      <FiltersDrawer opened={filtersOpened} onClose={closeFilters} onRemoveFilter={resetFilters}>
        <TableFilters
          filtersAvailable={availableFilters}
          filterObject={filterObject}
          onSubmit={applyFilters}
          initialValues={filters}
          tableName={tableName}
          initialFiltersName={filtersName}
          setFiltersName={mergeFilterName}
        />
      </FiltersDrawer>
      <CustomTableTitle
        title={title}
        actions={actions}
        tableName={tableName}
        submit={submit}
        invalidateViews={refreshViews}
      />
      <div className={styles('filters')}>
        {!isMobile && (
          <div className={styles('quick-search')}>
            <Input
              noMargin
              quickSearch
              type="text"
              placeholder={t(i18nKeys.FORM.QUICK_SEARCH.PLACEHOLDER)}
              onValueChanged={submit}
            />
          </div>
        )}
        <div className={styles('filters-actions')}>
          <Button
            small
            noMargin
            noShadow
            label={t(i18nKeys.SAVE_FILTERS)}
            color={ButtonColor.GREY}
            onClick={saveFilters}
          />
          {filtersNumber > 0 && (
            <Button
              className={styles('remove-filter', 'filter')}
              noMargin
              small
              noShadow
              iconSize="16px"
              color={ButtonColor.WHITE}
              onClick={resetFilters}
            >
              <Icon name={IconName.FILTER} />
              <Icon name={IconName.SMALL_REMOVE} />
            </Button>
          )}
          <Button
            noMargin
            small
            iconSize="16px"
            noShadow
            color={ButtonColor.WHITE}
            iconLeft={IconName.FILTER}
            onClick={openFilters}
          >
            {filtersNumber > 0 && (
              <div className={styles('text-circle', 'absolute', 'border', 'badge')}>
                {filtersNumber}
              </div>
            )}
            {t(i18nKeys.FILTER)}
          </Button>
        </div>
      </div>
      <div className={styles('filters-container')}>
        {filtersToShow.map(({ name, text, key, pairedKey }) => (
          <FilterItem
            key={key}
            label={name}
            value={
              Array.isArray(text)
                ? text.map(el => (filtersName[key] ? filtersName[key][el] : el)).join(', ')
                : text
            }
            onClick={openFilters}
            onClickDelete={() => removeFilter(key, pairedKey)}
          />
        ))}
      </div>
      <ViewTableGrid
        columns={currentView.table_columns}
        // A page that does not handle a selection has nothing to batch, so it
        // gets no selection column rather than a control that does nothing.
        isSelectable={Boolean(onChangeSelectionCallback)}
        items={items}
        pagination={pagination!}
        sortBy={filters.sort_by}
        sortOrder={filters.sort_order}
        onSort={field => setOrder(field)()}
        selectedIds={selectedIds}
        areAllItemsOnThisPageSelected={areAllItemsOnThisPageSelected}
        areAllItemsAcrossPagesSelected={areAllItemsAcrossPagesSelected}
        onToggleSelectAllOnPage={toggleSelectAllOnPage}
        onToggleAcrossPages={toggleAcrossPages}
        onToggleRow={toggleRow}
        isLoading={isLoading}
        isFetching={isFetching}
        filtersNumber={filtersNumber}
        noResultMessage={noResultMessage}
        pageLimit={filters.page_limit ?? profile.preferences.itemsPerPage}
        onClickRow={onClickRow}
        // actionsCol arrives as the orchestrator's loose `Function` prop; the grid types
        // it as a typed cell renderer. Bridge the legacy boundary with a single cast.
        actionsCol={actionsCol as ((item: TableRow) => ReactNode) | undefined}
        onEditColumns={setColumn}
        currentPage={currentPage}
        currentPageLimit={filters.page_limit}
        setPage={setPage}
        setPageLimit={setPageLimit}
      />
    </div>
  );
}
